// CHAT-PRESENCE (founder's design discussion 2026-10-29): what each DE is doing, as the ring on its
// avatar shows it, and the DM list that holds only open conversations.
//
//   dePresence.list   every DE of the company the caller can talk to, with its ring state for the
//                     caller (`needs_you` depends on who asks) and what the hover card shows:
//                     the run behind the state, its task, where it works, a screen it holds, the
//                     ask waiting on the caller, the first line of a failure. The state is
//                     company-wide knowledge (as the roster's live dot); the details are the
//                     caller's only where it may read them (the run, the channel, the approval).
//   dePresence.ack    "Got it" on a stuck DE: its latest failed run is acknowledged, for everyone.
//   dms.close         the hover × on a DM row: closed for the caller only, history kept. It opens
//                     again when anyone writes in it, or when the caller opens it (`dms.open`).
//   dms.cleanup       "Clean up" in the DM section: closes the caller's DMs quiet for 7, 14 or 30
//                     days, never one with a DE that needs the caller.
// The ring's priority rule is `deRingState` (@companyos/domain-work/chat, presence.ts).
import { z } from "zod";
import { Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const chat = { feature: "chat" } as const;

/**
 * A DE's ring, in priority order: `needs_you` (an ask or approval waits on the caller), `stuck`
 * (its latest run failed and nobody acknowledged it), `working` (a run is live), `waiting` (a run
 * waits, not on the caller), `available`, `off` (paused, onboarding or retired).
 */
export const DeRingState = z.enum(["needs_you", "stuck", "working", "waiting", "available", "off"]);
export type DeRingState = z.infer<typeof DeRingState>;

/** What waits on the caller, in one sentence, with what answers it. */
export const DePresenceAsk = z
  .object({
    /** `approval`: approve or reject; `input`: reply in its thread; `screen`: help on a screen. */
    kind: z.enum(["approval", "input", "screen"]),
    text: z.string(),
    approval_id: Uuid.nullable(),
    screen_lease_id: Uuid.nullable(),
    /** Where to reply (`input`), or where the ask's card is. */
    thread_id: Uuid.nullable(),
    /** `input` in a reply thread: the thread's root message (a reply goes under it). */
    parent_message_id: Uuid.nullable(),
  })
  .meta({ id: "DePresenceAsk" });

/** Where the run behind the state works: a channel the caller can view, and its topic. */
export const DePresencePlace = z
  .object({
    channel_id: Uuid,
    channel_name: z.string(),
    /** A DM: the channel name is the members' names. */
    is_dm: z.boolean(),
    topic_id: Uuid.nullable(),
    topic_name: z.string().nullable(),
  })
  .meta({ id: "DePresencePlace" });

export const DePresence = z
  .object({
    membership_id: Uuid,
    state: DeRingState,
    /** When the state began: the live run's start, the wait, the failure. Null when idle. */
    since: Timestamp.nullable(),
    /** The run behind the state, when the caller may view it. */
    run_id: Uuid.nullable(),
    task: z.object({ id: Uuid, title: z.string() }).nullable(),
    place: DePresencePlace.nullable(),
    /** A live screen the DE holds now (Screens). */
    screen_lease_id: Uuid.nullable(),
    ask: DePresenceAsk.nullable(),
    /** `stuck`: the failure's first line, when the caller may view the run. */
    error: z.string().nullable(),
  })
  .meta({ id: "DePresence" });
export type DePresence = z.infer<typeof DePresence>;

export const DePresenceList = z
  .object({ data: z.array(DePresence) })
  .meta({ id: "DePresenceList" });

/** Quiet DMs "Clean up" closes: no message for this many days. */
export const DmCleanupDays = z.union([z.literal(7), z.literal(14), z.literal(30)]);
export const DmCleanup = z.strictObject({ quiet_days: DmCleanupDays }).meta({ id: "DmCleanup" });
export const DmCleanupResult = z
  .object({ closed_channel_ids: z.array(Uuid) })
  .meta({ id: "DmCleanupResult" });

export const DmClosed = z
  .object({ channel_id: Uuid, closed: z.boolean(), closed_at: Timestamp.nullable() })
  .meta({ id: "DmClosed" });

export const chatPresenceRoutes = {
  "dePresence.list": defineRoute({
    ...chat,
    id: "dePresence.list",
    method: "get",
    path: `${base}/de-presence`,
    tag: "channels",
    summary: "Each DE's ring for the caller (needs you, stuck, working, waiting, available, off)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: DePresenceList },
    errors: [],
    emits: [],
  }),
  "dePresence.ack": defineRoute({
    ...chat,
    id: "dePresence.ack",
    method: "post",
    path: `${base}/de-presence/{id}/ack`,
    tag: "channels",
    summary: "Acknowledge a stuck DE's failed run (the ring stops showing it for everyone)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: DePresence },
    errors: [],
    emits: ["run.failure_acknowledged"],
  }),
  "dms.close": defineRoute({
    ...chat,
    id: "dms.close",
    method: "post",
    path: `${base}/dms/{id}/close`,
    tag: "channels",
    summary: "Close a DM in the caller's list (history kept; any new message opens it again)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: DmClosed },
    errors: [],
    emits: ["channel.dm_closed"],
  }),
  "dms.cleanup": defineRoute({
    ...chat,
    id: "dms.cleanup",
    method: "post",
    path: `${base}/dms/cleanup`,
    tag: "channels",
    summary: "Close the caller's DMs quiet for 7, 14 or 30 days (never one where a DE needs them)",
    access: "member",
    params: CompanyParams,
    body: DmCleanup,
    response: { status: 200, schema: DmCleanupResult },
    errors: [],
    emits: ["channel.dm_closed"],
  }),
} as const;
